/** EDP-00002 — Design tokens (single source of truth for Tailwind + CSS vars)
 * Shell CSS vars: apps/web/src/index.css (--shell-*). GLOBAL-SHELL-IMPLEMENTATION-000001
 */

export const EDP_DS_ID = 'EDP-00002';
export const FOUNDER_OS_SHELL_ID = 'GLOBAL-SHELL-IMPLEMENTATION-000001';

export const PRINCIPLES = [
  { id: 'ai_first', label: 'AI First', description: 'AI-native components and interactions' },
  { id: 'human_centered', label: 'Human Centered', description: 'Humans remain in control' },
  { id: 'enterprise_scale', label: 'Enterprise Scale', description: 'Dense data, multi-tenant' },
  { id: 'minimal_cognitive_load', label: 'Minimal Cognitive Load', description: 'Calm, focused interfaces' },
  { id: 'consistency', label: 'Consistency', description: 'One visual language everywhere' },
  { id: 'predictability', label: 'Predictability', description: 'Same patterns, same behavior' },
  { id: 'accessibility', label: 'Accessibility', description: 'WCAG 2.2 AA minimum' },
  { id: 'performance', label: 'Performance', description: 'Perceived speed via skeletons' },
  { id: 'responsiveness', label: 'Responsiveness', description: 'Mobile to ultra-wide' },
  { id: 'trust', label: 'Trust', description: 'Transparent status and audit' },
  { id: 'explainability', label: 'Explainability', description: 'AI reasoning visible' },
] as const;

export const COLORS = {
  brand: { 50: '#eef6ff', 500: '#2563eb', 600: '#1d4ed8', 700: '#1e40af' },
  teal: { 50: '#ccfbf1', 500: '#0d9488', 600: '#0f766e', 700: '#115e59' },
  success: { 50: '#ecfdf5', 500: '#10b981', 700: '#047857' },
  warning: { 50: '#fffbeb', 500: '#f59e0b', 700: '#b45309' },
  error: { 50: '#fef2f2', 500: '#ef4444', 700: '#b91c1c' },
  info: { 50: '#f0f9ff', 500: '#0ea5e9', 700: '#0369a1' },
  ai: { 50: '#f5f3ff', 500: '#7c3aed', 700: '#6d28d9' },
  neutral: { 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 500: '#64748b', 700: '#334155', 900: '#0f172a' },
} as const;

export const SPACING = [0, 4, 8, 12, 16, 24, 32, 48, 64] as const;

export const BREAKPOINTS = {
  mobile: 0,
  tablet: 768,
  laptop: 1024,
  desktop: 1280,
  ultra: 1920,
} as const;

/** EDP-00007 — minimum touch target (WCAG 2.2) */
export const TOUCH_TARGET_MIN_PX = 44;

/** EDP-00007 — UI density spacing scale multipliers */
export const DENSITY_SCALE = {
  Compact: 0.875,
  Comfortable: 1,
  Spacious: 1.125,
} as const;

export const TYPOGRAPHY = {
  display: 'text-3xl font-semibold leading-tight',
  h1: 'text-2xl font-semibold',
  h2: 'text-xl font-semibold',
  h3: 'text-lg font-semibold',
  body: 'text-sm',
  label: 'text-xs font-medium uppercase tracking-wide',
  caption: 'text-xs text-[var(--shell-fg-muted)]',
  kpi: 'text-2xl font-semibold',
  ai: 'text-sm leading-relaxed',
} as const;

export const ELEVATION = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
} as const;

export const RADIUS = {
  sm: 'rounded-md',
  md: 'rounded-lg',
  lg: 'rounded-xl',
  full: 'rounded-full',
} as const;

export type ThemeMode = 'light' | 'dark' | 'system';

/** EDP-00008 — motion duration tokens (ms) */
export const MOTION_DURATION = {
  instant: 0,
  fast: 100,
  normal: 200,
  slow: 300,
  slower: 400,
} as const;

/** EDP-00008 — easing curves */
export const MOTION_EASING = {
  default: 'ease',
  enter: 'ease-out',
  exit: 'ease-in',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

/** EDP-00008 — transition presets (Tailwind-compatible) */
export const MOTION_TRANSITIONS = {
  fade: `opacity ${MOTION_DURATION.normal}ms ${MOTION_EASING.enter}`,
  slideUp: `transform ${MOTION_DURATION.normal}ms ${MOTION_EASING.enter}, opacity ${MOTION_DURATION.normal}ms ${MOTION_EASING.enter}`,
  scale: `transform ${MOTION_DURATION.fast}ms ${MOTION_EASING.enter}`,
  press: `transform ${MOTION_DURATION.fast}ms ${MOTION_EASING.exit}`,
} as const;

/** EDP-00008 — max animation duration per performance guidelines */
export const MOTION_MAX_DURATION_MS = 400;

/** EDP-00008 — feedback must appear within this delay (ms) */
export const FEEDBACK_DELAY_MS = 100;

export const COMPONENT_CATEGORIES = [
  'Primitive',
  'AI',
  'Business',
  'Workspace',
  'Data',
  'Feedback',
  'Motion',
] as const;
